/**
 * Created by Ning Jiang on 10/10/2018.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {IParsedStackedSymbolName, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {IBetLineWinData, IBetWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {IWinRatioConfig, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {SequentiallyBetLineInitialWPController} from "@nolimitcity/slot-game/bin/game/winpresentation/initial/sequentially/SequentiallyBetLineInitialWPController";
import {Timeline, TimelineLite} from "gsap";
import {ThorParsedGameData} from "../../server/data/ThorParsedGameData";
import {ThorGameConfig} from "../../ThorGameConfig";
import {ThorSoundConfig} from "../../ThorSoundConfig";
import {ThorAnimatedWinlineController} from "../animatedwinlines/ThorAnimatedWinlineController";
import {ThorInitialWinCountUp} from "./ThorInitialWinCountUp";
import {ThorInitialWPView} from "./ThorInitialWPView";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";

export class ThorInitialWPController extends SequentiallyBetLineInitialWPController {

    private _reelsNum:number;
    private _symbolsNum:number;
    private _winCountUpBlue:ThorInitialWinCountUp;
    private _winCountUpYellow:ThorInitialWinCountUp;
    private _symbolWinStates:boolean[][];
    private _gameView:ThorInitialWPView;
    private _payoutMultiplierBefore:number;
    private _payoutMultiplierAfter:number;
    private _totalSpinWinningsNonMultiplied:number;
    private _totalSpinWinnings:number;
    private _currentWinRatioConfig:IWinRatioConfig;

    constructor(indexes:[number, number]) {
        super(indexes);

        this._gameView = new ThorInitialWPView();

        this._reelsNum = GameConfig.instance.REELS_NUM;
        this._symbolsNum = GameConfig.instance.SYMBOLS_NUM_IN_REEL[2];

        this._winCountUpYellow = new ThorInitialWinCountUp(ThorGameConfig.TEXT_STYLES.COUNTUP_YELLOW_BITMAP_STYLE);
        this._winCountUpYellow.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.5);
        StageManager.getLayer(ThorGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._winCountUpYellow);

        this._winCountUpBlue = new ThorInitialWinCountUp(ThorGameConfig.TEXT_STYLES.COUNTUP_BLUE_BITMAP_STYLE);
        this._winCountUpBlue.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.5);
        StageManager.getLayer(ThorGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._winCountUpBlue);
    }

    protected parseFeatureGameData(data:ThorParsedGameData):void {
        this._payoutMultiplierBefore = data.payoutMultiplierBefore;
        this._payoutMultiplierAfter = data.payoutMultiplierAfter;
        this._totalSpinWinningsNonMultiplied = data.totalSpinWinningsNonMultiplied;
        this._totalSpinWinnings = data.totalSpinWinnings;
        this._currentWinRatioConfig = data.currentWinRatioConfig!;
        this._symbolWinStates = ArrayHelper.initArrayWithValues<boolean[]>(this._reelsNum, () => {
            return ArrayHelper.initArrayWithValues<boolean>(this._symbolsNum, () => false)
        });

        this._betWinsData!.forEach((betWinData:IBetWinData) => {
            const betLineWinData:IBetLineWinData = <IBetLineWinData>betWinData;
            for(let reelId:number = 0; reelId < betLineWinData.nrOfSymbols; reelId++) {
                const symbolId:number = betLineWinData.betLine[reelId];
                this._symbolWinStates[reelId][symbolId] = true;

                if(GameConfig.instance.STACKED_SYMBOLS != null) {
                    // To mark all the stack win.
                    const parsedStackedSymbolName:IParsedStackedSymbolName = StateReelSymbol.parseStackedSymbolName(this._gameData!.reels[reelId][symbolId + GameConfig.instance.SYMBOLS_NUM_IN_REEL[1]], reelId);
                    if(parsedStackedSymbolName.isStacked) {
                        for(let i:number = 0; i < parsedStackedSymbolName.totalNum; i++) {
                            this._symbolWinStates[reelId][Math.min(GameConfig.instance.SYMBOLS_NUM_IN_REEL[2] - 1, Math.max(0, i - parsedStackedSymbolName.index + symbolId))] = true;
                        }
                    }
                }
            }
        });

        // #311
        ThorGameConfig.instance.WIN_BELOW_STAKE = (SlotGame.api.gameClientConfiguration.belowStakeWinRestriction && (data.singleWin <= data.playedBetValue));
    }

    protected startWinPresentation():void {
        // #311
        if (!ThorGameConfig.instance.WIN_BELOW_STAKE) {
            SlotGame.sound.playEffect(this.getWinTypeSound(this._gameData!.currentWinRatioConfig!.type!));
        }

        super.startWinPresentation();
    }

    private addAllNoWinSymbolsToTimeline(timeline:Timeline, duration:number):void {
        let noWinSymbol:StateReelSymbol | null;
        for(let i:number = 0; i < this._symbolWinStates.length; i++) {
            for(let j:number = 0; j < this._symbolWinStates[i].length; j++) {
                if(this._symbolWinStates[i][j]) {
                    continue;
                }
                noWinSymbol = Reels.getSymbol(i, j);
                if(noWinSymbol == null) {
                    continue;
                }

                if(noWinSymbol.symName && noWinSymbol.symName != "B" && noWinSymbol.symName != "S") {
                    timeline.add(noWinSymbol.changeState({
                        state : GameConfig.instance.SYMBOL_STATES.noWin!,
                        fadeInDuration : duration
                    }), 0);
                }
            }
        }
    }

    protected playAllNoWinSymbols(timeline:Timeline):void {
        this.addAllNoWinSymbolsToTimeline(timeline, 1.5)
    }

    protected getNextSymbolAnimationTimeOffset(duration:number):number {
        return 0.2;
    }

    protected getNextBetWinAnimationTimeOffset(lastSymbolDelay:number):number {
        return 0.3;
    }

    protected addWinCountUpToTimeline(timeline:TimelineLite):void {
        // #315
        const individualWinAmountTl: TimelineLite = this.showBetlineAmountInitialWin(this._gameData!.betWins);
        timeline.add(individualWinAmountTl, 0);
        timeline.addLabel("individualWinAmountShown", individualWinAmountTl.totalDuration());

        if(this._gameData!.isBigWin) {
            return;
        }

        const totalWin:number = this._gameData!.totalWin;
        const from:number = totalWin - this._gameData!.singleWin;
        const duration:number = this._gameData!.currentWinRatioConfig!.duration;

        const countUpTimeline:TimelineLite = new TimelineLite();
        if(this._payoutMultiplierAfter > 1) {
            countUpTimeline.add(() => this._winCountUpYellow.visible = !ThorGameConfig.instance.WIN_BELOW_STAKE); // #314
            countUpTimeline.add(() => this._winCountUpYellow.currentWinRatio = this._currentWinRatioConfig.type!);
            countUpTimeline.add(this._winCountUpYellow.getCountUpAnimation(0, this._totalSpinWinningsNonMultiplied, duration, this._gameData!.currentMultiplier, false, false));
            countUpTimeline.add(this._gameView.getMultiplyAnimationTL(this._payoutMultiplierAfter), "-=0.5");
            countUpTimeline.add([
                this._winCountUpBlue.currentWinRatio = this._currentWinRatioConfig.type!,
                this._winCountUpBlue.getCountUpAnimation(this._totalSpinWinningsNonMultiplied, this._totalSpinWinnings, duration, this._gameData!.currentMultiplier, false, false), "-=0.6",
                () => this._winCountUpYellow.visible = false
            ]);
        } else {
            countUpTimeline.add(() => this._winCountUpYellow.visible = !ThorGameConfig.instance.WIN_BELOW_STAKE); // #314
            countUpTimeline.add(() => this._winCountUpYellow.currentWinRatio = this._currentWinRatioConfig.type!);
            countUpTimeline.add(this._winCountUpYellow.getCountUpAnimation(0, this._gameData!.singleWin, duration, this._gameData!.currentMultiplier, false, false));
        }

        countUpTimeline.add(() => SlotGame.winFieldController.showWinField(totalWin));

        timeline.add(countUpTimeline, "individualWinAmountShown");
    }

    // Returns the offset time in the main timeline from this symbol animation start to when the next symbol animation should start.
    protected addSymbolWinAnimationToTimeline(betLineWinData:IBetLineWinData, timeline:Timeline, startTime:number, symbol:StateReelSymbol, reelId:number, symbolId:number):number {
        const symbolAnimation:Timeline = symbol.changeState({
            state : ThorGameConfig.instance.SYMBOL_STATES.initialWin,
            setStack : true,
            fadeInDuration : 0.1
        })!;

        timeline.add([
            () => this.sortSymbolDepthToTop(symbol),
            symbolAnimation
        ], 0.2);

        return this.getNextSymbolAnimationTimeOffset(0);
    }

    protected addSingleBetWinAnimationToTimeline(betLineWinData:IBetLineWinData, timeline:TimelineLite, startTime:number):number {
        const symbolsNum:number = betLineWinData.nrOfSymbols;
        let symbol:StateReelSymbol | null;
        let symbolDelay:number = 0;

        for(let i:number = 0; i < symbolsNum; i++) {
            symbol = this.findSymbol(i, betLineWinData.betLine[i]);
            if(!symbol) {
                debugger;
                throw new Error(`Error: SequentiallyBetLineInitialWPController.playBetWinPresentation(): Cannot find symbol at reel ${i} symbol ${betLineWinData.betLine[i]}`);
            }
            symbolDelay += this.addSymbolWinAnimationToTimeline(betLineWinData, timeline, startTime + symbolDelay, symbol!, i, betLineWinData.betLine[i]);
            if(this._winSymbols.indexOf(symbol) === -1) {
                this._winSymbols.push(symbol);
            }
        }
        if(this._betLineWinConfig.shouldPlayBetLine) {
            timeline.add(() => this.playBetLine!(betLineWinData.betLineIndex), 0);
        }

        return this.getNextBetWinAnimationTimeOffset(0);
    }

    protected abortWinPresentation() {
        if(this._timeline && this._timeline.isActive()) {
            this._timeline.progress(1);
        }
    }

    protected stopWinPresentation(isAborted:boolean):void {
        super.stopWinPresentation(isAborted);

        // #315
        StageManager.getLayer(ThorGameConfig.instance.LAYERS.INITIAL_WIN_AMOUNT_BET_LINES.name).removeChildren();

        EventHandler.dispatchEvent(new GameEvent(ThorAnimatedWinlineController.CLEAN_UP_ANIMATED_WINLINE));

        if(this._payoutMultiplierAfter > 1) {
            this._winCountUpBlue.abort(this._gameData!.singleWin, isAborted, this._gameData!.currentMultiplier, this._gameData!.isBigWin);
            this._winCountUpYellow.abort(this._gameData!.singleWin, isAborted, this._gameData!.currentMultiplier, this._gameData!.isBigWin);
        } else {
            this._winCountUpYellow.abort(this._gameData!.singleWin, isAborted, this._gameData!.currentMultiplier, this._gameData!.isBigWin);
        }

        const stopTimeline:TimelineLite = new TimelineLite();
        // For abort before initial win start.
        this.addAllNoWinSymbolsToTimeline(stopTimeline, 0);
        if(!this._gameData!.isBigWin) {
            SlotGame.winFieldController.showWinField(this._gameData!.totalWin);
        }
    }

    protected playBetLine(betLineNumber:number):void {
        EventHandler.dispatchEvent(new GameEvent(ThorAnimatedWinlineController.PLAY_ANIMATED_WINLINE, betLineNumber));
    }

    private getWinTypeSound(winType:WinRatioType):string {
        switch(winType) {
            case WinRatioType.LARGE :
                return ThorSoundConfig.instance.WIN_LARGE;

            case WinRatioType.MEDIUM :
                return ThorSoundConfig.instance.WIN_MEDIUM;

            case WinRatioType.SMALL :
                return ThorSoundConfig.instance.WIN_SMALL;

            case WinRatioType.TINY :
                return ThorSoundConfig.instance.WIN_TINY;

            case WinRatioType.BIG_WIN :
                return ThorSoundConfig.instance.BIG_WIN_INITIAL_WIN;

            case WinRatioType.MEGA_WIN :
                return ThorSoundConfig.instance.BIG_WIN_INITIAL_WIN;

            case WinRatioType.SUPER_MEGA_WIN :
                return ThorSoundConfig.instance.BIG_WIN_INITIAL_WIN;

            default :
                return "";
        }
    }

    // #315
    private showBetlineAmountInitialWin(betWins: IBetWinData[]): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            container: PIXI.Container = new PIXI.Container();

        if (ThorGameConfig.instance.WIN_BELOW_STAKE || !SlotGame.api.gameClientConfiguration.displayIndividualWinsInGame) {
            return tl;
        }

        container.position.set(640, 360);
        container.alpha = 0;

        betWins.forEach((betWin: IBetWinData, idx: number) => {
            const winAmount: PIXI.Text = new PIXI.Text(betWin.multipliedAmount.toFixed(2), new PIXI.TextStyle({
                fontFamily: OpenSans.FAMILY,
                fontSize: 36,
                fontWeight: FontWeight.BOLD,
                fill: "#fff"
            }));

            winAmount.y = (idx * 40);

            if (betWins.length > 20) {
                winAmount.anchor.set(0.5, 0);
            }

            container.addChild(winAmount);
        });

        if (betWins.length > 13) {
            container.scale.set(0.65);
            container.y -= 90;
        }
        container.pivot.set(container.width / 2, container.height / 2);

        StageManager.getLayer(ThorGameConfig.instance.LAYERS.INITIAL_WIN_AMOUNT_BET_LINES.name).addChild(container);

        tl.to(container, 0.1, {
            alpha: 1
        });
        tl.to(container, 0.5, {
            y: "-=50"
        });
        tl.to(container, 0.1, {
            alpha: 0
        });
        tl.add(() => {
            container.removeChildren();
            StageManager.getLayer(ThorGameConfig.instance.LAYERS.INITIAL_WIN_AMOUNT_BET_LINES.name).removeChild(container);
        });

        return tl;
    }
}